import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { useAuth } from 'common/auth/auth-provider'
import Logo from 'components/ui/logo'
import { LogoTypeEnum } from 'components/ui/logo/interfaces'
import UserDetailForm from 'components/ui/user-detail-form'
import { useState } from 'react'
import ThemeAdmin from 'styles/theme-admin'
import ThemeSite from 'styles/theme-site'
import ForgotPasswordForm from './ForgotPasswordForm'
import LoginForm from './LoginForm'
import { LoginActionEnum, LoginActionType } from './interfaces'
import Constants from 'common/constants'
import { CircularProgress } from '@mui/material'

const formClass = {
  borderRadius: '0.25rem',
  display: 'flex',
  flexDirection: 'column',
  margin: '0 auto',
  maxWidth: {
    xs: '100%',
    sm: '480px',
    md: '480px'
  },
  width: {
    xs: '100%',
    sm: '480px',
    md: '480px'
  },
  padding: {
    xs: '16px',
    sm: '2rem',
    md: '2rem'
  },
  '@media (min-width: 430px)': {
    padding: '20px',
    paddingLeft: '0'
  },
  '@media (max-width: 430px)': {
    maxWidth: '320px',
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px',
    '& > *': {
      width: '100%',
      maxWidth: '320px'
    }
  },
  '@media (max-width: 375px)': {
    maxWidth: '280px',
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px',
    '& > *': {
      width: '100%',
      maxWidth: '280px'
    }
  }
}

export default function Login({
  initialAction = LoginActionEnum.login
}: {
  initialAction?: LoginActionEnum
}) {
  const theme = useTheme()
  const [action, setAction] = useState<LoginActionType>(initialAction)
  const queryparams = new URLSearchParams(window.location.search)
  const isAdminModule = Boolean(queryparams.get('admin'))
  const auth = useAuth()
  const ComponentWrapper = () => {
    switch (action) {
      case LoginActionEnum.login:
        return (
          <LoginForm
            isAdminModule={isAdminModule}
            onClickForgotPasswordButton={() =>
              setAction(LoginActionEnum.forgotPassword)
            }
            onClickCreateUserButton={() => {
              setAction(LoginActionEnum.createUser)
            }}
          />
        )

      case LoginActionEnum.forgotPassword:
        return (
          <ForgotPasswordForm
            setAction={() => setAction(LoginActionEnum.login)}
          />
        )

      case LoginActionEnum.createUser:
        return (
          <UserDetailForm
            isCreateCustomer={true}
            onCancel={() => {
              setAction(LoginActionEnum.login)
            }}
            onSuccess={(_updatedUser, token, refreshToken) => {
              if (token) {
                auth.loginWithToken(token, refreshToken || '')
              }
            }}
            onClose={() => {
              setAction(LoginActionEnum.login)
            }}
          />
        )
      case LoginActionEnum.loadingPermisions:
        return (
          <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <CircularProgress color='error' />
          </Box>
        )
    }
  }

  const Theme = isAdminModule ? ThemeAdmin : ThemeSite

  return (
    <Theme>
      <Box
        sx={{
          minHeight: '100vh',
          height: '100vh',
          width: '100vw',
          position: 'fixed',
          top: 0,
          left: 0,
          justifyContent: 'end',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          backgroundImage: `url(${Constants.BACKGROUND_IMAGE_LOGIN})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          '@media (max-width: 768px)': {
            backgroundAttachment: 'scroll'
          }
        }}
      >
        <Box
          sx={{
            minHeight: '100vh',
            height: '100vh',
            justifyContent: 'center',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            padding: {
              xs: '16px',
              sm: '24px',
              md: '32px'
            },
            '@media (max-width: 430px)': {
              alignItems: 'center',
              padding: '16px 8px'
            },
            '@media (max-width: 375px)': {
              alignItems: 'center',
              padding: '12px 4px'
            }
          }}
        >
          <Box
            sx={{
              color: isAdminModule
                ? theme.palette.secondary.contrastText
                : theme.palette.primary.dark,
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              marginBottom: {
                xs: '24px',
                sm: '32px',
                md: '40px'
              }
            }}
          >
            <Logo
              type={isAdminModule ? LogoTypeEnum.LIGHT : LogoTypeEnum.DARK}
            />
          </Box>
          <Box sx={formClass}>
            <Theme>
              <ComponentWrapper />
            </Theme>
          </Box>
          {/* <Box sx={copyrightClass}>
            <p>{I18n.login.copyright(currentDate.getFullYear())}</p>
          </Box> */}
        </Box>
        <Box
          sx={{
            backgroundImage: `url(${Constants.IMAGE_YOUX})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            height: {
              xs: '60px',
              sm: '80px',
              md: '100px'
            },
            marginTop: 'auto'
          }}
        ></Box>
      </Box>
    </Theme>
  )
}
